// import React, { useEffect, useState } from 'react';
// import { Card, Statistic, Row, Col, Button, Alert, Typography, Space, Spin } from 'antd';
// import { ProjectOutlined, CodeOutlined, HistoryOutlined, ThunderboltOutlined } from '@ant-design/icons';
// import { projectsService, skillGroupsService, experiencesService } from '../firebase/services';
// import { seedFirestore } from '../firebase/seed';

// const { Title, Paragraph } = Typography;

// export default function OverviewPage() {
//   const [stats,   setStats]   = useState({ projects: 0, skillGroups: 0, experiences: 0 });
//   const [loading, setLoading] = useState(true);
//   const [seeding, setSeeding] = useState(false);
//   const [seedMsg, setSeedMsg] = useState('');

//   // ── Load counts ────────────────────────────────────────────
//   const loadStats = async () => {
//     setLoading(true);
//     try {
//       const [p, s, e] = await Promise.all([
//         projectsService.getAll(),
//         skillGroupsService.getAll(),
//         experiencesService.getAll(),
//       ]);
//       setStats({ projects: p.length, skillGroups: s.length, experiences: e.length });
//     } catch (err) {
//       console.error('loadStats error:', err);
//     } finally {
//       setLoading(false);
//     }
//   };

//   // ── Auto-seed on first load if Firestore is empty ──────────
//   useEffect(() => {
//     let cancelled = false;

//     async function initData() {
//       setLoading(true);
//       try {
//         // Try to seed (no-op if already seeded)
//         const result = await seedFirestore();
//         if (!cancelled) {
//           if (result.seeded) {
//             setSeedMsg('success:Firestore seeded automatically on first load!');
//           }
//           await loadStats();
//         }
//       } catch (err) {
//         if (!cancelled) {
//           console.warn('Auto-seed failed, loading existing data:', err.message);
//           await loadStats();
//         }
//       }
//     }

//     initData();
//     return () => { cancelled = true; };
//   }, []);

//   // ── Manual seed button ─────────────────────────────────────
//   const handleSeed = async () => {
//     setSeeding(true);
//     setSeedMsg('');
//     try {
//       const result = await seedFirestore();
//       setSeedMsg(
//         result.alreadySeeded
//           ? 'info:Data already exists in Firestore — no changes made.'
//           : 'success:Seed complete! All collections populated.'
//       );
//       if (result.seeded) loadStats();
//     } catch (e) {
//       setSeedMsg('error:Seed failed: ' + e.message);
//     } finally {
//       setSeeding(false);
//     }
//   };

//   const [msgType, msgText] = seedMsg ? seedMsg.split(/:(.+)/) : [];

//   return (
//     <>
//       <div style={{ marginBottom: 24 }}>
//         <Title level={4} style={{ margin: 0 }}>Dashboard Overview</Title>
//         <Paragraph type="secondary" style={{ margin: '4px 0 0' }}>
//           Manage your portfolio content — projects, skills, and experience.
//         </Paragraph>
//       </div>

//       {loading ? <Spin /> : (
//         <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
//           <Col xs={24} sm={8}>
//             <Card>
//               <Statistic
//                 title="Projects"
//                 value={stats.projects}
//                 prefix={<ProjectOutlined />}
//                 valueStyle={{ color: '#c8f04a' }}
//               />
//             </Card>
//           </Col>
//           <Col xs={24} sm={8}>
//             <Card>
//               <Statistic
//                 title="Skill Groups"
//                 value={stats.skillGroups}
//                 prefix={<CodeOutlined />}
//                 valueStyle={{ color: '#00e5ff' }}
//               />
//             </Card>
//           </Col>
//           <Col xs={24} sm={8}>
//             <Card>
//               <Statistic
//                 title="Experience Entries"
//                 value={stats.experiences}
//                 prefix={<HistoryOutlined />}
//                 valueStyle={{ color: '#a78bfa' }}
//               />
//             </Card>
//           </Col>
//         </Row>
//       )}

//       <Card title="Seed Initial Data" style={{ maxWidth: 500 }}>
//         <Paragraph type="secondary">
//           First time setup? Click below to populate Firestore with your portfolio data.
//           This is safe to run multiple times — it won't overwrite existing data.
//         </Paragraph>
//         <Space direction="vertical" style={{ width: '100%' }}>
//           <Button
//             type="primary"
//             icon={<ThunderboltOutlined />}
//             onClick={handleSeed}
//             loading={seeding}
//           >
//             Seed Firestore with Portfolio Data
//           </Button>
//           {msgText && (
//             <Alert type={msgType} message={msgText} showIcon />
//           )}
//         </Space>
//       </Card>
//     </>
//   );
// }
import React, { useEffect, useState } from 'react';
import { Card, Statistic, Row, Col, Button, Alert, Typography, Space, Spin } from 'antd';
import {
  ProjectOutlined, CodeOutlined, HistoryOutlined,
  ThunderboltOutlined, UserOutlined, SafetyCertificateOutlined,
} from '@ant-design/icons';
import { projectsService, skillGroupsService, experiencesService } from '../firebase/services';
import { REST_BASE, API_KEY } from '../firebase/config';
import { seedFirestore } from '../firebase/seed';

const { Title, Paragraph } = Typography;

// Quick REST fetch for collections not in services.js
async function restCount(col) {
  const res = await fetch(`${REST_BASE}/${col}?key=${API_KEY}&pageSize=300`);
  if (res.status === 404) return 0;
  if (!res.ok) return 0;
  const json = await res.json();
  return json.documents ? json.documents.length : 0;
}

export default function OverviewPage() {
  const [stats,   setStats]   = useState({ projects:0, skillGroups:0, experiences:0, about:0, certificates:0 });
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [seedMsg, setSeedMsg] = useState('');

  const loadStats = async () => {
    setLoading(true);
    try {
      const [p, s, e, a, c] = await Promise.all([
        projectsService.getAll(),
        skillGroupsService.getAll(),
        experiencesService.getAll(),
        restCount('about'),
        restCount('certificates'),
      ]);
      setStats({ projects:p.length, skillGroups:s.length, experiences:e.length, about:a, certificates:c });
    } catch (err) {
      console.error('loadStats error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    async function initData() {
      setLoading(true);
      try {
        const result = await seedFirestore();
        if (!cancelled) {
          if (result.seeded) setSeedMsg('success:Firestore seeded automatically on first load!');
          await loadStats();
        }
      } catch (err) {
        if (!cancelled) {
          console.warn('Auto-seed failed, loading existing data:', err.message);
          await loadStats();
        }
      }
    }
    initData();
    return () => { cancelled = true; };
  }, []);

  const handleSeed = async () => {
    setSeeding(true);
    setSeedMsg('');
    try {
      const result = await seedFirestore();
      setSeedMsg(
        result.alreadySeeded
          ? 'info:Data already exists in Firestore — no changes made.'
          : 'success:Seed complete! All collections populated.'
      );
      if (result.seeded) loadStats();
    } catch (e) {
      setSeedMsg('error:Seed failed: ' + e.message);
    } finally {
      setSeeding(false);
    }
  };

  const [msgType, msgText] = seedMsg ? seedMsg.split(/:(.+)/) : [];

  const STAT_CARDS = [
    { key:'projects',     label:'Projects',      icon:<ProjectOutlined />,             color:'#c8f04a' },
    { key:'skillGroups',  label:'Skill Groups',  icon:<CodeOutlined />,                color:'#00e5ff' },
    { key:'experiences',  label:'Experience',    icon:<HistoryOutlined />,             color:'#a78bfa' },
    { key:'about',        label:'About Doc',     icon:<UserOutlined />,                color:'#ff6b6b' },
    { key:'certificates', label:'Certificates',  icon:<SafetyCertificateOutlined />,   color:'#f59e0b' },
  ];

  return (
    <>
      <div style={{ marginBottom: 24 }}>
        <Title level={4} style={{ margin: 0 }}>Dashboard Overview</Title>
        <Paragraph type="secondary" style={{ margin: '4px 0 0' }}>
          Manage your portfolio content — projects, skills, experience, about, and certificates.
        </Paragraph>
      </div>

      {loading ? <Spin /> : (
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          {STAT_CARDS.map(({ key, label, icon, color }) => (
            <Col xs={24} sm={12} md={8} lg={8} xl={4} key={key}>
              <Card>
                <Statistic
                  title={label}
                  value={stats[key]}
                  prefix={icon}
                  valueStyle={{ color }}
                />
              </Card>
            </Col>
          ))}
        </Row>
      )}

      <Card title="Seed Initial Data" style={{ maxWidth: 520 }}>
        <Paragraph type="secondary">
          First time setup? Click below to populate Firestore with all portfolio data
          (projects, skills, experience, about, and certificates).
          Safe to run multiple times — won't overwrite existing data.
        </Paragraph>
        <Space direction="vertical" style={{ width: '100%' }}>
          <Button
            type="primary"
            icon={<ThunderboltOutlined />}
            onClick={handleSeed}
            loading={seeding}
          >
            Seed Firestore with Portfolio Data
          </Button>
          {msgText && <Alert type={msgType} message={msgText} showIcon />}
        </Space>
      </Card>
    </>
  );
}